<script setup>
import { computed, onMounted, ref } from 'vue';
import { OVERVIEW_METRICS } from '../constants';
import { useToggle } from '@vueuse/core';

import DetailedOpenConversationsTable from './overview/DetailedOpenConversationsTable.vue';
import DetailedAgentsTable from './overview/DetailedAgentsTable.vue';
import MetricCard from './overview/MetricCard.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const uiFlags = useMapGetter('getOverviewUIFlags');
const agentStatus = useMapGetter('agents/getAgentStatus');
const accountConversationMetric = useMapGetter('getAccountConversationMetric');
const store = useStore();

const accounti18nKey = 'OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS';
const teams = useMapGetter('teams/getTeams');

const teamMenuList = computed(() => {
  return [
    { label: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.ALL_TEAMS'), value: null },
    ...teams.value.map(team => ({ label: team.name, value: team.id })),
  ];
});

const showConversationMetricDetails = ref(false);
const showAgentMetricDetails = ref(false);

const agentStatusMetrics = computed(() => {
  let metric = {};
  Object.keys(agentStatus.value).forEach(key => {
    const metricName = t(
      `OVERVIEW_REPORTS.AGENT_STATUS.${OVERVIEW_METRICS[key]}`
    );
    metric[metricName] = agentStatus.value[key];
  });
  return metric;
});
const conversationMetrics = computed(() => {
  let metric = {};
  Object.keys(accountConversationMetric.value).forEach(key => {
    const metricName = t(`${accounti18nKey}.${OVERVIEW_METRICS[key]}`);
    metric[metricName] = Object.values(
      accountConversationMetric.value[key]
    ).reduce((sum, value) => sum + value, 0);
  });
  return metric;
});

const selectedTeam = ref(null);
const selectedTeamLabel = computed(() => {
  const team =
    teamMenuList.value.find(
      menuItem => menuItem.value === selectedTeam.value
    ) || {};
  return team.label;
});
const fetchData = () => {
  const params = {};
  if (selectedTeam.value) {
    params.team_id = selectedTeam.value;
  }
  store.dispatch('fetchAccountConversationMetric', params);
};

const { startRefetching } = useLiveRefresh(fetchData);
const [showDropdown, toggleDropdown] = useToggle();

const handleAction = ({ value }) => {
  toggleDropdown(false);
  selectedTeam.value = value;
  fetchData();
};

const toggleConversationMetricDetails = () => {
  showConversationMetricDetails.value = !showConversationMetricDetails.value;
};

const toggleAgentMetricDetails = () => {
  showAgentMetricDetails.value = !showAgentMetricDetails.value;
};

onMounted(() => {
  fetchData();
  startRefetching();
});
</script>

<template>
  <div class="flex flex-col items-center md:flex-row gap-4">
    <div
      class="flex-1 w-full max-w-full md:w-[60%] md:max-w-[60%] conversation-metric"
    >
      <MetricCard
        :header="t(`${accounti18nKey}.HEADER`)"
        :is-loading="uiFlags.isFetchingAccountConversationMetric"
        :loading-message="t(`${accounti18nKey}.LOADING_MESSAGE`)"
      >
        <template #control>
          <div
            v-if="teams.length"
            v-on-clickaway="() => toggleDropdown(false)"
            class="relative flex items-center group z-50"
          >
            <Button
              sm
              slate
              faded
              :label="selectedTeamLabel"
              class="capitalize rounded-md group-hover:bg-n-alpha-2"
              @click="toggleDropdown()"
            />
            <DropdownMenu
              v-if="showDropdown"
              :menu-items="teamMenuList"
              class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full"
              label-class="capitalize"
              @action="handleAction($event)"
            />
          </div>
          <Button
            sm
            slate
            faded
            :label="t('OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.VIEW_DETAILS')"
            class="capitalize rounded-md group-hover:bg-n-alpha-2"
            @click="toggleConversationMetricDetails()"
          />
        </template>
        <div class="flex-1">
          <div class="flex justify-between gap-2 pb-2">
            <div
              v-for="(metric, name, index) in conversationMetrics"
              :key="index"
              class="min-w-0"
            >
              <h3 class="text-base text-n-slate-11">
                {{ name }}
              </h3>
              <p class="text-n-slate-12 text-3xl mb-0 mt-1">
                {{ metric }}
              </p>
            </div>
          </div>
          <DetailedOpenConversationsTable
            v-if="showConversationMetricDetails"
            :detailed-conversation-metrics="accountConversationMetric"
          />
        </div>
      </MetricCard>
    </div>
    <div class="flex-1 w-full max-w-full md:w-[40%] md:max-w-[40%]">
      <MetricCard :header="$t('OVERVIEW_REPORTS.AGENT_STATUS.HEADER')">
        <template #control>
          <Button
            sm
            slate
            faded
            :label="t('OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.VIEW_DETAILS')"
            class="capitalize rounded-md group-hover:bg-n-alpha-2"
            @click="toggleAgentMetricDetails()"
          />
        </template>
        <div class="flex-1">
          <div class="flex justify-between gap-2 pb-2">
            <div
              v-for="(metric, name, index) in agentStatusMetrics"
              :key="index"
              class="min-w-0"
            >
              <h3 class="text-base text-n-slate-11">
                {{ name }}
              </h3>
              <p class="text-n-slate-12 text-3xl mb-0 mt-1">
                {{ metric }}
              </p>
            </div>
          </div>
          <DetailedAgentsTable v-if="showAgentMetricDetails" />
        </div>
      </MetricCard>
    </div>
  </div>
</template>
